<template>
  <div class="ms-combobox ms-editor ms-validate">
    <label
      v-if="label"
      :class="`label ${isRequired ? 'required' : ''}`"
    >
      {{ label }}
    </label>
    <div
      class="flex-row combo-box-input"
      :class="[
        { error: errorMessage, disabled: disabled },
        hasBorder ? 'border' : '',
        type
      ]"
    >
      <div
        v-if="leftIcon"
        :class="['icon24 left-icon', leftIcon]"
      />
      <input
        ref="input"
        v-model="internalText"
        :title="internalText"
        class="input flex"
        :placeholder="placeholder"
        :disabled="disabled"
        :readOnly="readOnly || selectOnly"
        :maxlength="maxLength"
        :class="[
          { disabled: disabled },
          type
        ]"
        v-on="listeners"
        @keydown.esc="onEsc"
        @keydown.enter="onEnter"
        @keydown.f9="showQuickAdd(false, true)"
      >
      <!-- Khi không config thêm nhanh nhiều object trong combobox -->
      <div
        v-if="quickAdd"
        class="quickAdd"
        :class="{ hide: readOnly || disabled }"
        @click="showQuickAdd"
        @keydown.enter="showQuickAdd"
      />

      <!-- Menu khi click icon thêm nhanh nhiều object trong combobox -->
      <menu-wrapper>
        <template #menu-button="{ toggleMenu }">
          <!-- Khi có config thêm nhanh nhiều object trong combobox -->
          <div
            v-if="quickAddMulti"
            class="quickAdd quickAddMulti"
            :class="{
              disabled: disabled,
              'point-event-none': readOnly,
              hide: readOnly || disabled,
            }"
            @click="toggleMenu"
            @keydown.enter="toggleMenu"
          />
        </template>
        <template #menu-content>
          <menu-item
            v-for="(item, index) in quickAddMulti"
            :key="index"
            @menu-item-click="showQuickAdd(false, false, item.valueField)"
          >
            {{ item.displayField }}
          </menu-item>
        </template>
      </menu-wrapper>

      <div
        class="ms-combo-box__icon-dropdown"
        :class="[
          {
            disabled: disabled,
            'point-event-none': readOnly,
            expand: expanded,
          },
        ]"
        @click="triggerClick"
      >
        <div class="icon20-v2 drop-down" />
      </div>
    </div>

    <div
      v-if="errorMessage && isShowErrorMessage && !isGridEditor"
      class="error-text"
    >
      {{ errorMessage }}
    </div>

    <ms-combo-box-dropdown
      v-if="expanded"
      ref="dropdownPanel"
      :topx="dropdownTopx"
      :leftx="dropdownLeftx"
      :max-widthx="dropdownMaxWidthx"
      :min-widthx="dropdownMinWidthx"
      :columnx="columns"
      :combo-type="comboType"
      :highligh-index="typeAheadPointer"
      :scroll-top="dropdownScrollTop"
      :loading="loading"
      :quick-add="quickAdd"
      :quick-search="quickSearch"
      @scrollend="dropdownScrollEnd"
      @addNew="showQuickAdd"
    >
      <ms-combo-box-item
        v-for="(item, index) in internalDataSource"
        :key="item[valueField]"
        :combo-type="comboType"
        :item="item"
        :is-selected="item && item[valueField] == modelValue"
        :columnx="columns"
        :value-field="valueField"
        :display-field="displayField"
        :class="{ 'ms-combobox-item--highlight': typeAheadPointer == index }"
        @mousedown="onItemClick(item, $event)"
      />
    </ms-combo-box-dropdown>
  </div>
</template>
<script>
import { defineComponent, getCurrentInstance, ref } from 'vue'
import lodashDebounce from 'lodash/debounce'

import MsComboBoxDropdown from './src//MsComboBoxDropdown.vue'
import MsComboBoxItem from './src/MsComboBoxItem.vue'
import MenuItem from '@/components/dropdown/MenuItem.vue'
import MenuWrapper from '@/components/dropdown/MenuWrapper.vue'
import commonFn from '@/commons/commonFunction.js'
import MsBaseComponent from '@/components/base/MsBaseComponent.vue'
import EventBusGlobal, { GlobalEventName } from '@/commons/eventBusGlobal'
import { filter } from '@/mixins/common/filter'
import { useValidateControl } from '@/setup/validateControl.js'
import popupUtil from '@/commons/popupUtil.js'
import { ComboType } from './src/constants'

import {
  UPDATE_MODEL_EVENT,
  UPDATE_DISPLAY_EVENT,
  CHANGE_EVENT,
  INPUT_EVENT,
  BLUR_EVENT,
  SELECTED_EVENT,
  CLICK_EVENT,
} from '@/components/utils/constants'

export default defineComponent({
  name: 'MsCombobox',
  components: {
    MsComboBoxDropdown,
    MsComboBoxItem,
    MenuWrapper,
    MenuItem,
  },
  extends: MsBaseComponent,
  mixins: [filter],
  props: {
    // Dữ liệu của combobox
    data: {
      default: () => [],
      type: Array,
    },
    /** v-model - Dữ liệu đã được chọn theo valueField */
    modelValue: {
      type: String || Number,
      default: null,
    },
    /** v-model:display - dữ liệu đã được chọn theo displayField */
    display: {
      type: [String, Number],
      default: null,
    },
    /** Tên trường làm value */
    valueField: {
      type: String,
      default: null,
    },
    /** Trường làm text display */
    displayField: {
      type: String,
      default: null,
    },
    /** placeholder */
    placeholder: {
      default: null,
      type: [Number, String],
    },
    /** Số item hiển thị tối đa */
    maxItemsDisplay: {
      default: 5,
      type: Number,
    },
    /** Cấu hình cột */
    columns: {
      type: Array,
      default: () => [],
    },
    /** Trường làm text hiển thị dạng code - name */
    prefixField: {
      type: String,
      default: null,
    },
    /** Trường giá trị ngăn cách */
    seperateChar: {
      type: String,
      default: '-',
    },
    /** Độ rộng của 1 item */
    itemSize: {
      default: 28,
      type: Number,
    },
    /** Chỉ nhận giá trị có trong danh sách */
    forceSelection: {
      default: true,
      type: Boolean,
    },
    /** Là combotree hay không */
    isTree: {
      default: false,
      type: Boolean,
    },
    /** Parent Field */
    idProperty: {
      default: 'id',
      type: String,
    },
    /** Field chứa parentID */
    parentIdProperty: {
      default: 'parent_id',
      type: String,
    },
    /** Không cho phép chọn trường là cha */
    notSelectionParent: {
      default: false,
      type: Boolean,
    },
    /** Độ rông của dropdown tính bằng px */
    dropdownWidth: {
      default: null,
      type: [String, Number],
    },
    /** Mode query lấy dữ liệu từ server hay client */
    queryMode: {
      type: String,
      default: 'local',
    },
    remoteFilter: {
      type: Boolean,
      default: false,
    },
    /**  Gán giá trị text lúc ban đầu */
    initText: {
      default: null,
      type: [Number, String],
    },
    /** chỉ cho phép chọn không cho phép nhập text */
    selectOnly: {
      default: false,
      type: Boolean,
    },
    autoLoad: {
      default: false,
      type: Boolean,
    },
    mappingField: {
      default: null,
      type: Array || Object,
    },
    keyField: {
      default: null, // '__vKeyValue'
      type: String,
    },
    maxLength: {
      default: null,
      type: Number,
    },
    leftIcon: {
      type: String,
      default: null,
    },
    /** Câu hình quicksearch */
    quickSearch: {
      type: String,
      default: null,
    },
    /** Dữ liệu truyền kèm quicksearch */
    quickSearchParam: {
      type: Object,
      default: null,
    },
    /** Câu hình add */
    quickAdd: {
      type: String,
      default: null,
    },
    /** Cấu hình add multi form */
    quickAddMulti: {
      type: Array,
      default: null,
    },
    /** có show error mesage không */
    isShowErrorMessage: {
      type: Boolean,
      default: true,
    },
    /** Danh sách các trường dữ liệu lấy về */
    requestField: {
      type: String,
      default: null,
    },
    /** custom lại tham số filter */
    buildFilter: {
      type: Function,
      default: null,
    },
    /** size paging */
    pageSize: {
      type: Number,
      default: 10,
    },
    /** Giá trị bản ghi tất cả */
    selectAllValue: {
      type: [Number, String],
      default: -1,
    },
    /** Text hiển thị tất cả */
    selectAllText: {
      type: String,
      default: null,
    },
    /** Có hiển thị bản ghi tất cả không */
    showSelectAll: {
      type: Boolean,
      default: false,
    },
    /** Xác định xem input có border hay không? */
    hasBorder: {
      default: true,
      type: Boolean,
    },
    /**
     * Xác định các rule validate
     * Mặc định của combo sẽ là force select
     */
    rules: {
      default: () => [{ name: 'forceSelection' }],
      type: Array,
    },
    /**
     * trường cho phép có được nhập text tự do trên combo hay không
     * cho phép bind dữ liệu vào combo nhưng không bind vào dropdown
     */
    isFreeText: {
      default: false,
      type: Boolean,
    },
    /** Có phải là đang được dùng trong grid không */
    isGridEditor: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'default',
    },
  },
  emits: [
    'msKeydown',
    'comboboxLoadData',
    BLUR_EVENT,
    'beforeQuery',
    UPDATE_MODEL_EVENT,
    'update:suffixDisplay',
    UPDATE_DISPLAY_EVENT,
    'update:prefix',
    SELECTED_EVENT,
    'beforeExpand',
    CHANGE_EVENT,
    CLICK_EVENT,
    INPUT_EVENT,
  ],

  setup (props) {
    const { proxy: me } = getCurrentInstance()
    const {
      errorMessage,
      handlerValidateCombobox,
      getTitleError,
      clearValidate,
      isRequired,
    } = useValidateControl({
      props,
    })

    const validate = () => {
      if (!props.isGridEditor) {
        return handlerValidateCombobox()
      }
      return ''
    }
    const isShowFullText = ref(false)

    return {
      errorMessage,
      validate,
      getTitleError,
      clearValidate,
      isShowFullText,
      isRequired,
    }
  },
  data () {
    const me = this
    me.initStaticData()

    return {
      internalDataSource: [],
      internalText: null,
      expanded: false,
      dropdownTopx: 0,
      dropdownLeftx: 0,
      internalSelectedItem: null,
      typeAheadPointer: -1,
      focused: false,
      gridItemSize: 48, // kích thước mặc định của itemSize của combox khi được dùng trên lưới Editor
      tooltipLeft: 0,
      dropdownScrollTop: 0,
      loading: false, // Đang load dữ liệu
      lastRequestParam: null, // Tham số load dữ liệu lần gần nhất
      dropdownMinWidthx: null,
      dropdownMaxWidthx: null,
    }
  },
  computed: {
    listeners () {
      const me = this
      return {
        input: (event) => me.onInput(event),
        focus: (event) => me.onFocus(event),
        change: (event) => me.onChange(event),
        blur: (event) => me.onBlur(event),
        keydown: (event) => me.onKeydown(event),
        click: (event) => me.onClick(event),
      }
    },
  },

  watch: {
    data: {
      inmediate: true,
      handler (newVal) {
        const me = this

        if (me.loading) {
          me.loading = false

          me.$nextTick(() => {
            me.doHightlightItem()
          })
        }

        if (newVal == null) {
          // tình huống có thêm/sửa/xóa dữ liệu gốc -> reset để khi expand sẽ load lại dữ liệu
          me.lastQueryString = new Date().getTime()
          me._allData = null
        } else {
          if (me.queryMode !== 'remote') {
            me._allData = newVal
          }

          if (
            me.internalDataSource !== newVal &&
            !(me.internalDataSource.length === 0 && newVal.length === 0)
          ) {
            me.initData()
          }
        }
      },
    },
    /**
     * Set lại giá trị hiển thị khi giá trị của biến initText thay đổi
     */
    initText: {
      immediate: true,
      handler (newVal, oldVal) {
        const me = this
        if (newVal || oldVal) {
          me.setInitText(newVal)
        }
      },
    },

    modelValue: {
      immediate: true,
      handler (newVal) {
        const me = this
        me.setDisplayText(newVal)
      },
    },
    // Khi thay đổi số phần tử của combox thì thiết lập lại vị trí của dropdown
    internalDataSource: {
      immediate: true,
      handler () {
        const me = this
        me.setDisplayText(me.modelValue)
        // ĐVThi sửa lỗi 90491 không thay đổi vị trí khi gõ text để search trên combox
        this.changeDropdownPosition()
      },
    },
  },
  // Hàm khởi tạo dữ liệu hook func
  created () {
    const me = this
    me.initIndex = 0

    me.initCombo()
    me.initData()
    if (me.autoLoad) {
      me.doQueryInternal(null, false)
    }
    EventBusGlobal.$on(GlobalEventName.scrollEvent, me.scrollEvent)
  },
  mounted () {
    const me = this
    me.reload = true
    // Debounce query để delay loadva
    if (me.queryMode !== 'remote') {
      me.internalQueryDelay = 0
    }
    me.doQuery = lodashDebounce(me.doQueryInternal, me.internalQueryDelay)
    document.addEventListener(CLICK_EVENT, me.clickOutSite)

    // init alltext
    me._selectAllText =
      me.selectAllText || me.$t('i18nComponent.Combobox.SelectAllItemText')
  },
  beforeUnmount () {
    const me = this
    me.collapse()
    if (me.$refs.dropdownPanel) {
      me.$refs.dropdownPanel.$el.remove()
    }
    EventBusGlobal.$off(GlobalEventName.scrollEvent, me.scrollEvent)
    document.removeEventListener(CLICK_EVENT, me.clickOutSite)
  },
  methods: {
    setDisplayText (value) {
      const me = this
      if (value !== undefined) {
        var displayText = me.getDisplayText(me.modelValue)
        if (displayText) {
          me.setInitText(displayText)
        }
      }
    },

    getDisplayText (modelVal) {
      const me = this
      const item = me.internalDataSource?.find(x => {
        return x[me.valueField] === modelVal
      })
      if (item) {
        return item[me.displayField]
      }
      return null
    },

    getValue () {
      const me = this
      return me.modelValue
    },

    /**
     * Bắt sự kiện khi scroll trên chương trình thì đóng combobox lại
     * Created by LTDAT
     */
    scrollEvent () {
      const me = this
      if (me.expanded) {
        me.collapse()
      }
    },

    /**
     * Mặc định chọn giá trị theo index ban đầu
     * Created by LTDAT
     */
    setInitIndex () {
      const me = this
      if (
        me.initIndex >= 0 &&
        me.internalDataSource &&
        me.internalDataSource.length > 0 &&
        me.typeAheadPointer < 0
      ) {
        me.typeAheadPointer = me.initIndex
        me.setInternalItem(me.internalDataSource[me.typeAheadPointer], false)
      }
    },
    /**
     * Set giá trị hiện thị ban đầu cho combobox khi combo chưa có dữ liệu
     * Created by LTDAT(25.06.2020)
     */
    setInitText (val) {
      const me = this
      me.internalText = me.initText ? me.initText : val
    },
    /**
     * Thực hiện query dữ liệu, có thể từ local hoặc remote tùy theo cấu hình
     * CreatedBy LTDAT 11/12/2019
     */
    doQueryInternal (searchString, expand) {
      const me = this
      const event = { preventDefault: false, sender: me }
      let queryString = searchString
      me.$emit('beforeQuery', event)
      if (event.preventDefault) {
        return false
      }

      // Đoạn này check xem nếu khác querystring thì thực hiện load data từ server
      if (me.lastQueryString === queryString && !me.reload) {
        if (expand) {
          me.expand()
        }
      } else {
        // Nếu có queryString thì thực hiện reset data về ban đầu
        if (queryString) {
          me.dropdownScrollTop = 0
          me.internalDataSource = []
          me.lastQueryString = queryString
          me.querying = true
        }

        if (me.queryMode === 'remote' && !me._allData) {
          if (me.isTree) {
            queryString = null
          }
          me.lastQueryString = queryString
          me.doRemoteQuery(queryString)
        } else {
          me.$nextTick(() => {
            // HTHIEP 01.07.2022: Tạm trường hợp có subffix k hỗ trợ filter local
            me.doLocalQuery(queryString)
            if (expand && !me.expanded) {
              me.expand()
            } else {
              me.doHightlightItem(true)
            }
          })
        }
        me.reload = false
      }
    },

    /**
     * Xử lý sau khi query xong dữ liệu
     * CreatedBy LTDAT 24.06.2020
     */
    afterQuery (expand) {
      const me = this
      if (expand) {
        me.expand()
      }
    },
    /**
     * Query dữ liệu api
     * CreatedBy LTDAT 24.06.2020
     */
    doRemoteQuery (queryString) {
      // Build filter,
      const me = this
      const filters = []
      const filterFields = me.filterFields || {}
      return new Promise((resolve, reject) => {
        try {
          if (queryString) {
            const fields = Object.keys(filterFields)
            // HTHIEP 01.07.2022: Xử lý query trường hợp có subffix
            if (
              queryString &&
              me.prefixField &&
              queryString.contains(me.seperateChar)
            ) {
              const queryStrings = queryString.trim().split(me.seperateChar)
              const filter = {
                Field: fields[0],
                Operator: filterFields[fields[0]],
                Value: queryStrings[0].trim(),
              }
              filter.Ors = []
              filters.push(filter)
              if (fields.length > 1) {
                for (let i = 1; i < fields.length; i++) {
                  filter.Ors.push({
                    Field: fields[i],
                    Operator: filterFields[fields[i]],
                    Value: queryStrings[0].trim(),
                  })
                }
              }
              for (let i = 1; i < queryStrings.length; i++) {
                const queryValue = queryStrings[i]
                if (queryValue) {
                  filter.Ors.push({
                    Field: fields[0],
                    Operator: filterFields[fields[0]],
                    Value: queryValue.trim(),
                  })
                  if (fields.length > 1) {
                    for (let i = 1; i < fields.length; i++) {
                      filter.Ors.push({
                        Field: fields[i],
                        Operator: filterFields[fields[i]],
                        Value: queryValue.trim(),
                      })
                    }
                  }
                }
              }
            } else {
              const filter = {
                Field: fields[0],
                Operator: filterFields[fields[0]],
                Value: queryString,
              }
              filters.push(filter)
              if (fields.length > 1) {
                filter.Ors = []
                for (let i = 1; i < fields.length; i++) {
                  filter.Ors.push({
                    Field: fields[i],
                    Operator: filterFields[fields[i]],
                    Value: queryString,
                  })
                }
              }
            }
          }
          const payload = {}
          payload.skip = 0
          payload.take = me.pageSize
          payload.pageAppend = me.lastRequestParam
            ? me.lastRequestParam.pageAppend
            : false
          if (filters.length > 0) {
            payload.filter = JSON.stringify(filters)
          }

          let columns = [me.displayField, me.valueField]
          if (me.columns && me.columns.length > 0) {
            me.columns.forEach((item) => {
              if (item.dataField && columns.indexOf(item.dataField) === -1) {
                columns.push(item.dataField)
              }
            })
          }

          if (me.mappingField) {
            for (const i in me.mappingField) {
              if (columns.indexOf(i) === -1) {
                columns.push(i)
              }
            }
          }

          if (me.requestField) {
            me.requestField.split(',').forEach((item) => {
              if (item && columns.indexOf(item)) {
                columns.push(item)
              }
            })
          }
          columns = columns.distinctKey(columns)

          payload.columns = columns.join(',')

          me.lastRequestParam = payload

          if (typeof me.buildFilter === 'function') {
            me.buildFilter(payload)
          }
          me.loading = true
          // trường hợp có query string ko cần phải lấy selected item
          if (me.modelValue && !queryString) {
            Object.assign(payload, {
              selectedItem: JSON.stringify({
                [me.valueField]: me.modelValue,
              }),
            })
          }
          me.$emit(me.$ms.constant.CustomEvent.MsComboboxLoadData, payload)
          resolve()
        } catch (error) {
          reject(error)
        }
      })
    },
    /**
     * Query dữ liệu local
     * CreatedBy LTDAT 24.06.2020
     */
    doLocalQuery (queryString) {
      const me = this
      let filterData = me.filterData(queryString)
      if (queryString && me.comboType === ComboType.Tree) {
        // thêm cha vào để build cây
        let roots = []
        let i = 0
        while (i < filterData.length) {
          const item = filterData[i]
          const parent = item.parentNode

          if (!parent || Array.isArray(parent)) {
            roots.push(item)
          } else if (filterData.indexOf(parent) === -1) {
            filterData.push(parent)
          }
          i++
        }

        // sắp xếp root theo đúng thứ tự
        if (roots.length > 1) {
          const rs = []
          for (let i = 0; i < me._allData.length; i++) {
            const item = me._allData[i]
            if (roots.indexOf(item) > -1) {
              rs.push(item)

              if (rs.length === roots.length) {
                roots = rs
                break
              }
            }
          }
        }

        // build cây
        const rs = []
        const fnPush = function (item) {
          rs.push(item)
          if (Array.isArray(item.childNodes)) {
            item.childNodes.forEach((child) => {
              if (filterData.indexOf(child) > -1) {
                fnPush(child)
              }
            })
          }
        }
        roots.forEach((item) => {
          fnPush(item)
        })

        // sắp xếp theo misaCode
        filterData = rs
      }

      me._filterData = filterData
      const count = Math.min(me._filterData?.length, me.pageSize)
      const ds = []
      for (let i = 0; i < count; i++) {
        ds.push(me._filterData[i])
      }

      me.internalDataSource = ds
    },
    filterData (queryString) {
      const me = this
      let matchItem = []

      if (queryString) {
        me._allData?.forEach((item) => {
          let valid = false
          for (const f in me.filterFields) {
            const value = item[f]
            const test = me.testFilter(me.filterFields[f], value, queryString)
            if (test) {
              valid = true
              break
            }
          }

          if (valid) {
            matchItem.push(item)
          }
        })
      } else {
        matchItem = me._allData
      }
      return matchItem
    },

    /**
     * Lấy chuỗi cần query, trường hợp number thì lấy value bỏ qua ký tự phân cách
     * CreatedBy LTDAT 11/12/2019
     */
    getQueryString () {
      return (this.internalText || '').trim()
    },
    // Click ra ngoài combo đóng combo lại
    clickOutSite (e) {
      const me = this
      if (
        !commonFn.hasParent(e.target, me.$el) &&
        me.$refs.dropdownPanel &&
        me.$refs.dropdownPanel.$el &&
        !commonFn.hasParent(e.target, me.$refs.dropdownPanel.$el)
      ) {
        me.collapse()
      }
    },
    // Reset lại các giá trị trong combobox
    reset () {
      const me = this
      me.internalText = ''
      me.internalSelectedItem = null
      me.typeAheadPointer = -1
      me.internalValue = null
      me.$emit(UPDATE_MODEL_EVENT, me.internalValue)
      me.$emit(UPDATE_DISPLAY_EVENT, '')
    },
    /**
     * Set giá trị hiển thị
     * @pram value giá trị hiển thị
     */
    setInternalText (value) {
      const me = this
      me.internalText = value
    },
    /**
     * Set value cho combo
     */
    setValue (value, userAction = false) {
      const me = this
      me.internalValue = value
      me.$emit(UPDATE_MODEL_EVENT, me.internalValue)
      me.doHightlightItem()
      if (me.typeAheadPointer >= 0) {
        me.setInternalItem(
          me.internalDataSource[me.typeAheadPointer],
          userAction
        )
      }
    },
    // Sự kiện nhập liệu trên input
    // Created by LTDAT (16.06.2020)
    onInput () {
      const me = this

      me._typing = true
      if (!me.expanded) {
        me.expand()
        // me.doQueryInternal(me.getQueryString(), true);
      }

      if (!me.internalText) {
        const metaData = {
          oldData: me.internalSelectedItem,
          newData: null,
        }
        me.$emit(SELECTED_EVENT, metaData)
        me.reset()
      }

      me.doQuery(me.internalText, true)
      me._typing = false
      if (
        !me.rules.some(
          (x) => x.name === me.$ms.constant.ValidateRule.ForceSelection
        ) &&
        me.valueField === me.displayField
      ) {
        me.internalValue = me.internalText
        if (me.isGridEditor) {
          me.$emit(UPDATE_DISPLAY_EVENT, me.internalValue)
        } else {
          me.$emit(UPDATE_MODEL_EVENT, me.internalValue)
        }
      }
      me.doHightlightItem(true)
      me.adjustScroll()
    },
    // Sự kiện click vào input
    onClick (e) {
      this.$emit(CLICK_EVENT, e)
    },
    // Sự kiện focus vào input
    // Created by LTDAT (16.06.2020)
    onFocus () {
      const me = this
      me.focused = true
    },
    // Sự kiện change input
    // Created by LTDAT (16.06.2020)
    onChange () {
      // const me = this;
      // if (!me.focused) {
      //   return
      // }

      // if (me.forceSelection) {
      //   if (me.typeAheadPointer < 0) {
      //     me.reset();
      //     let dataEmpty = {};
      //     dataEmpty[me.valueField] = null;
      //     dataEmpty[me.displayField] = me.internalText;
      //     let metaData = {
      //       oldData: me.internalSelectedItem,
      //       newData: dataEmpty,
      //       userAction: false
      //     };
      //     //TODO bnduc 9.7.2021: Bỏ raise selectd event đi vì các form đang handle để gán lại initText -> lỗi validate forceSelection
      //     me.$emit("selected", metaData);
      //     me.$emit(UPDATE_MODEL_EVENT, me.internalValue);
      //   } else {
      //     me.typeAheadPointer = me.filterIndexItemByText();
      //   }
      // } else {
      //   if (me.valueField == me.displayField) {
      //     me.internalValue = me.internalText;
      //     me.$emit(UPDATE_MODEL_EVENT, me.internalValue);
      //   } else {
      //     me.typeAheadPointer = me.filterIndexItemByText();
      //     if (me.typeAheadPointer < 0) {
      //       me.internalValue = null;
      //       let dataEmpty = {};
      //       dataEmpty[me.valueField] = null;
      //       dataEmpty[me.displayField] = me.internalText;
      //       let metaData = {
      //         oldData: me.internalSelectedItem,
      //         newData: dataEmpty,
      //         userAction: false,
      //       };
      //       me.$emit(SELECTED_EVENT, metaData);
      //       me.$emit(UPDATE_MODEL_EVENT, me.internalValue);
      //     }
      //   }
      // }
    },
    // Sự kiện blur input
    onBlur (e) {
      const me = this
      me.focused = false

      this.$nextTick(() => {
        me.validateBlur()
      })
      me.$emit('blur', e)
    },

    // Sự kiện keydown input
    onKeydown (e) {
      const me = this
      const keyCodes = me.$ms.constant.KeyCode
      e.stopPropagation()

      // Nếu bấm 2 mũi tên lên xuống mà đang mở dropdown thì ko emit ra ngoài để xử lý
      let isEmitOut = true
      switch (e.which) {
        case keyCodes.Down:
          if (!me.expanded) {
            // Thêm expand khi thực hiện query dữ liệu mới
            me.expanded = true
            me.doQueryInternal(null, true)
          } else {
            isEmitOut = false
            if (me.typeAheadPointer < me.getTotalData() - 1) {
              if (me.notSelectionParent) {
                const typeAheadPointerNext = me.typeAheadPointer + 1
                for (
                  let i = typeAheadPointerNext;
                  i < me.getTotalData();
                  i++
                ) {
                  me.typeAheadPointer++
                  me.adjustScroll(typeAheadPointerNext)
                  if (!me.internalDataSource[i].isParent) {
                    break
                  }
                }
              } else {
                me.typeAheadPointer++
                me.adjustScroll()
              }
            }
          }
          break
        case keyCodes.Up:
          if (me.expanded) {
            isEmitOut = false
            if (me.typeAheadPointer > 0) {
              if (me.notSelectionParent) {
                const typeAheadPointerPre = me.typeAheadPointer - 1
                for (let i = typeAheadPointerPre; i > 0; i--) {
                  me.typeAheadPointer--
                  me.adjustScroll(me.typeAheadPointer)
                  if (!me.internalDataSource[i].isParent) {
                    break
                  }
                }
              } else {
                me.typeAheadPointer--
                me.adjustScroll()
              }
            }
          }
          break
        case keyCodes.Enter:
          if (me.typeAheadPointer >= 0) {
            me.setInternalItem(me.internalDataSource[me.typeAheadPointer])
          }
          me.collapse()
          break
        case keyCodes.Tab:
          if (me.expanded) {
            if (me.typeAheadPointer >= 0) {
              me.setInternalItem(me.internalDataSource[me.typeAheadPointer])
            }
            me.collapse()
          }
          break
      }
      if (isEmitOut) {
        me.$emit(this.$ms.constant.CustomEvent.MsKeydown, e)
      }
    },
    // Lấy tổng số lượng dữ liệu trong data
    getTotalData () {
      const me = this
      return me.internalDataSource.length
    },
    // Bôi màu hightlight cho item
    doHightlightItem (ignoreValue) {
      const me = this
      let index = -1

      if (me.internalDataSource && me.internalDataSource.length > 0) {
        index = -1

        if (
          !ignoreValue &&
          me.internalValue !== null &&
          me.internalValue !== undefined
        ) {
          const sources = me._allData || me.internalDataSource

          // Trước khi load tính toán lại typeAheadPointer nếu cần thiết để scroll di chuyển về dúng vị trí khi show scroller lên
          // Tìm vị trí selectedItem trong source, không lấy selectedIndex vì có thể không chính xác khi source thay đổi
          for (let i = 0; i < sources.length; i++) {
            const item = sources[i]
            if (item[me.valueField] === me.internalValue) {
              index = i
              break
            }
          }
        }

        if (index === -1 && me.internalText) {
          const lowerText = me.internalText.toLowerCase()

          if (me.columns && me.columns.length > 0) {
            for (let i = 0; i < me.internalDataSource.length; i++) {
              const item = me.internalDataSource[i]
              for (let j = 0; j < me.columns.length; j++) {
                const field = me.columns[j].dataField
                const itemValue = item[field]
                if (
                  typeof itemValue === 'string' &&
                  itemValue.toLowerCase().indexOf(lowerText) > -1
                ) {
                  index = i
                  break
                }
              }

              if (index > -1) {
                break
              }
            }
          } else {
            for (let i = 0; i < me.internalDataSource.length; i++) {
              const itemValue = me.internalDataSource[i][me.displayField]
              if (
                typeof itemValue === 'string' &&
                itemValue.toLowerCase().indexOf(lowerText) > -1
              ) {
                index = i
                break
              }
            }
          }
        }
      }

      me.typeAheadPointer = index
    },
    /**
     * Tìm item theo text truyền vào
     * HTHIEP 01.07.2022
     */
    findItemByText (queryText) {
      if (!queryText || !this.internalDataSource?.length) {
        return false
      }

      return this.internalDataSource.filter((item) => {
        let text = item[this.displayField]
        if (item[this.prefixField]) {
          text = `${item[this.prefixField]} ${this.seperateChar} ${
            item[this.displayField]
          }`
        }
        if (queryText === text) {
          return item
        }
      })
    },
    // Tìm kiếm item trong list
    // Created by LTDAT(16.06.2020)
    filterIndexItemByText (queryText) {
      const me = this
      const filters = me.filterFields
      let value = queryText || me.internalText
      let index = -1
      if (filters && value) {
        value = value.toString()
        index = me.internalDataSource.findIndex((item) => {
          let ret = false
          for (const key in filters) {
            // eslint-disable-next-line no-prototype-builtins
            if (filters.hasOwnProperty(key)) {
              const s = filters[key]
              if (s === me.$ms.constant.FilterOperator.Contains) {
                ret =
                  item[key] &&
                  item[key].toString() &&
                  item[key]
                    .toString()
                    .toLowerCase()
                    .contains(value.toLowerCase())
              } else {
                ret =
                  item[key] &&
                  item[key].toString() &&
                  item[key]
                    .toString()
                    .toLowerCase()
                    .startsWith(value.toLowerCase())
              }
              if (ret) {
                break
              }
            }
          }
          return ret
        })
      }
      return index
    },
    // Lấy cột filter để tìm kiếm local
    // Created by LTDAT 16.06.2020
    getFilterFields () {
      const filter = {}
      const me = this
      if (this.columns && this.columns.length > 0) {
        for (let i = 0; i < this.columns.length; i++) {
          const col = this.columns[i]
          if (!col.filterOperator) {
            filter[this.displayField] = me.$ms.constant.FilterOperator.Contains
          }
          if (col.dataField && col.filterOperator) {
            if (this.filterColumns && this.filterColumns.length > 0) {
              this.filterColumns.forEach((e) => {
                // eslint-disable-next-line no-prototype-builtins
                if (!filter.hasOwnProperty(e)) {
                  filter[e] = col.filterOperator
                }
              })
            } else {
              filter[col.dataField] = col.filterOperator
            }
          }
        }
      } else {
        filter[this.displayField] = me.$ms.constant.FilterOperator.Contains
      }
      return filter
    },
    // Lấy element scroll
    // Created by LTDAT (16.06.2020)
    getScroller () {
      if (this.expanded) {
        // return this.$refs["scroller"];
        const dd = this.$refs.dropdownPanel
        if (dd && dd.$el && typeof dd.$el.querySelector === 'function') {
          return dd.$el.querySelector('.scroller-overlay')
        }
      }
      return null
    },
    /**
     * Di chuyển view đến bản ghi nếu cần
     * @param {Bản ghi cần view} index
     * CreatedBy LTDAT 16.06.2020
     */
    adjustScroll (typeAheadPointer = this.typeAheadPointer) {
      const row = this.$refs.dropdownPanel?.$el?.querySelector('.combobox-item')

      if (row) {
        const rowHeight = row.getBoundingClientRect().height
        const scroller = this.getScroller()
        if (scroller) {
          // scroller.scrollToItem(typeAheadPointer, true);
          scroller.scrollTop =
            (Number.parseInt(rowHeight) - 1) * typeAheadPointer
        }
      }
    },
    onScrollerVisible () {
      const me = this
      me.adjustScroll()
    },
    // Hàm xử lý khi chọn item trên dropdown
    // Created by LTDAT(16.06.2020)
    onItemClick (item, event) {
      const me = this
      event.preventDefault()
      me.setInternalItem(item)
      me.$refs.input.focus()
    },

    // Set giá trị được chọn
    setInternalItem (item) {
      const me = this
      if (item) {
        if (me.notSelectionParent && item.isParent) {
          return
        }
        // Lưu lại thông tin old item
        const oldItem = {
          [me.valueField]: me.internalValue,
          [me.displayField]: me.internalText,
        }

        if (!me.isFreeText) {
          me.internalSelectedItem = item
          me.internalValue = item[me.valueField]
        }

        if (me.prefixField) {
          const prefixValue = item[me.prefixField]

          me.internalText = `${prefixValue} ${me.seperateChar} ${item[me.displayField]}`
          me.$emit('update:prefix', prefixValue)
        } else {
          me.internalText = item[me.displayField]
        }
        me.$emit(UPDATE_DISPLAY_EVENT, item[me.displayField])
        me.$emit(UPDATE_MODEL_EVENT, me.internalValue) // NOTE: Update value sau khi update display mục đích để ghi đè giá trị trong TH là combo Enum
        me.typeAheadPointer = me.findIndexItem(item)

        me.$emit(SELECTED_EVENT, {
          oldData: oldItem,
          newData: item,
        })

        me.collapse()
      }
    },
    // Tìm vị trị của item trong data
    findIndexItem (item) {
      const me = this
      return me.internalDataSource.findIndex((_item) => {
        return _item === item
      })
    },
    // Khởi tạo dữ liệu tĩnh mặc định
    initStaticData () {
      const me = this
      me.comboType = ComboType.Normal

      me.internalQueryDelay = 200
      me.filterFields = me.getFilterFields()
      me.internalValue = null
      me.firstExpand = true
    },
    /**
     * thêm bản ghi chọn tất cả vào source
     */
    appendSelectAllItem () {
      const me = this
      if (!me.showSelectAll) {
        return
      }
      const firstItem = me.internalDataSource[0]
      if (firstItem && firstItem[me.valueField] !== me.selectAllValue) {
        const allItem = {}
        allItem[me.valueField] = me.selectAllValue
        allItem[me.displayField] = me._selectAllText

        // vvkiet - 24.06.2021: Bổ sung text Tất cả cho tất cả các cột
        me.columns.forEach((column) => {
          allItem[column.dataField] = me._selectAllText
        })

        me.internalDataSource.unshift(allItem)
      }
    },
    // append và check đã tồn tại chưa
    appendData (data, appendHead = false) {
      const dataQuery =
        this.queryMode === 'remote' ? this.internalDataSource : this._allData
      const validData = data.filter(
        (x) => !dataQuery.some((y) => y[this.valueField] === x[this.valueField])
      )
      appendHead
        ? this.internalDataSource.unshift(...validData)
        : this.internalDataSource.append(validData)
    },
    // Khởi tạo dữ liệu
    initData () {
      const me = this
      if (me.lastRequestParam && me.lastRequestParam.pageAppend) {
        me.appendData(me.data)
        // nếu dữ liệu trả về ít hơn mong đợi -> đánh dấu kết thúc để khi scroll xuống k load thêm nữa
        if (me.data.length < me.pageSize) {
          me.lastRequestParam.end = true
        } else {
          me.lastRequestParam.end = false
        }
      } else {
        me.internalDataSource = [...me.data]

        me.appendSelectAllItem()
      }
      if (me.data?.length > 0) {
        const record = me.internalDataSource.find((item) =>
          (item[me.valueField] === me.modelValue)
        )
        me.setInternalItem(record)
      }

      if (me.keyField && me.keyField !== me.valueField) {
        commonFn.genVKeyValue(me.internalDataSource, me.keyField)
      }
      // Nếu là combo dạng tree
      if (me.comboType === ComboType.Tree) {
        const data = JSON.parse(JSON.stringify(me.internalDataSource))
        const dataTree = commonFn.nomalizeDataTree(
          data,
          me.idProperty || me.valueField,
          me.parentIdProperty
        )
        const treeData = commonFn.getBodyData(dataTree, true)

        if (treeData.length > 0) {
          me._allData = treeData
        } else {
          me.internalDataSource = []
          me._allData = null
        }
      } else if (
        !me.remoteFilter &&
        me.internalDataSource &&
        me.internalDataSource.length > 0
      ) {
        me._allData = me.internalDataSource
        me.internalDataSource = []
      }

      if (me._allData) {
        me.doLocalQuery(me.lastQueryString)
      }
    },
    /**
     * Khởi tạo combo
     *
     * @author hngiap - 01.09.2019
     * 1 là combo không có columns (ComboEditor)
     * 2 là combo có columns
     * 3 là combo tree
     */
    initCombo () {
      const me = this
      if (me.isTree) {
        me.comboType = ComboType.Tree
      } else if (me.columns && me.columns.length !== 0) {
        me.comboType = ComboType.Table
      } else {
        me.comboType = ComboType.Normal
      }
    },

    /**
     * click vào icon dropdown
     */
    triggerClick () {
      const me = this
      const expanded = me.expanded

      // disable thì không làm gì
      if (me.disabled) {
        return
      }

      if (!expanded) {
        me.doQueryInternal(null, true)
        me.changeDropdownPosition()
        me.doHightlightItem()
      }

      // đặt nextick để tránh lỗi render dom của combobox tree khi chọn item từ dropdown
      me.$nextTick(() => {
        me.expanded = !expanded
      })

      // focus input
      me.$refs.input.focus()
    },

    /**
     * hiển thị dropdown
     */
    expand () {
      const me = this

      me.$emit('beforeExpand')
      me.changeDropdownPosition()
      me.doHightlightItem()

      // đặt nextick để tránh lỗi render dom của combobox tree khi chọn item từ dropdown
      me.$nextTick(() => {
        me.expanded = true
      })
    },
    /**
     * ẩn dropdown
     */
    collapse () {
      const me = this
      me.$nextTick(() => {
        me.expanded = false
      })
    },
    /**
     * Hàm set vị trí cho dropdown menu khi expand
     * TODO: Chưa xử lý khi có scroll
     */
    changeDropdownPosition () {
      const me = this
      let comboEl = me.$el

      if (comboEl) {
        comboEl = comboEl.getElementsByClassName('combo-box-input')[0]
      }

      if (comboEl) {
        const comboElRect = comboEl.getBoundingClientRect()

        const windowWidth =
          window.innerWidth ||
          document.documentElement.clientWidth ||
          document.body.clientWidth

        const windowHeight =
          window.innerHeight ||
          document.documentElement.clientHeight ||
          document.body.clientHeight

        me.dropdownLeftx = comboElRect.left
        me.dropdownTopx = comboElRect.top + comboElRect.height + 2
        me.dropdownMaxWidthx = me.dropdownMinWidthx = me.dropdownWidth
          ? me.dropdownWidth
          : comboElRect.width

        // Nếu chạm phải thì align sang trái
        if (comboElRect.left + me.dropdownMaxWidthx > windowWidth) {
          me.dropdownLeftx = comboElRect.right - me.dropdownMaxWidthx
        }

        // Nếu chạm bottom thì align top
        let maxItemsDisplay = me.maxItemsDisplay
        if (me.columns && me.columns.length > 0) {
          maxItemsDisplay += 1
        }
        if (
          comboElRect.bottom + me.itemSize * maxItemsDisplay + 2 >
          windowHeight
        ) {
          me.dropdownTopx =
            comboElRect.top -
            (me.itemSize *
              Math.min(maxItemsDisplay, me.internalDataSource.length) +
              30)
        }
      }
    },
    /**
     * Click vào icon quicksearch
     */
    quickSearchClick (e) {
      const me = this
      e.preventDefault()
      if (me.quickSearch) {
        if (me.disabled) {
          return
        }
        me.expanded = false
        const param = { ...(me.quickSearchParam || {}) }
        popupUtil.show(
          me.quickSearch,
          Object.assign(param, {
            /**
             * Sự kiện callback khi chọn item từ form tìm nhanh
             * Modified By NXCHIEN 01.06.2022 Sửa lại do base xử lý check options.submit
             */
            options: {
              submit: me.callbackAddData.bind(me),
            },
            close: function () {
              me.$refs.input.focus()
            },
          })
        )
      }
    },

    callbackAddData (record) {
      const me = this
      let notExistArr = []
      const checkingData = me._allData ? me._allData : me.internalDataSource
      if (Array.isArray(record)) {
        notExistArr = record.filter(
          (x) =>
            !checkingData.some((y) => y[me.valueField] === x[me.valueField])
        )

        me.setInternalItem(record[0])
      } else {
        if (
          !checkingData.find((i) => i[me.valueField] === record[me.valueField])
        ) {
          notExistArr = [record]
        }

        me.setInternalItem(record)
      }
      // me.$nextTick(() => {
      //   me.validate(me);
      // });

      me.$refs.input.focus()
      // Thêm data vào luôn dữ liệu hiện tại
      if (!me.isFreeText) {
        me.internalDataSource.unshift(...notExistArr)
        if (!me.lastRequestParam) {
          me.lastRequestParam = { pageAppend: true }
        } else {
          me.lastRequestParam.pageAppend = true
        }
      }
    },
    /**
     * Validate khi blur
     * ntnghia - 11/03/2021
     * bnduc 21.6.2021: Đổi lại thành gọi hàm validate của control thay vì viết scustom như này
     */
    validateBlur () {
      const me = this

      if (!me.isFreeText) {
        me.validate(this)
      }
    },
    /**
     * scroll xuống cuối drowndown -> load thêm dữ liệu
     */
    dropdownScrollEnd () {
      const me = this
      if (me.queryMode === 'remote' && !me._allData) {
        me.scrollDoing = true
        if (me.lastRequestParam && !me.lastRequestParam.end) {
          // TODO bnduc 21.6.2021: ghi nhận tính huống gọi vào đây 2 lần trên máy qc -> thêm kiểm tra này để k lỗi
          if (
            me.lastRequestParam.skip &&
            me.lastRequestParam.skip === me.internalDataSource.length
          ) {
            return
          }

          const payload = me.lastRequestParam
          payload.skip = me.internalDataSource.length
          payload.pageAppend = true
          me.$emit(me.$ms.constant.CustomEvent.MsComboboxLoadData, payload)
        }
      } else {
        const data = me._filterData || me._allData
        if (data) {
          me.internalDataSource = me.internalDataSource || []
          const start = me.internalDataSource.length
          const count = Math.min(data.length, me.pageSize + start)
          for (let i = start; i < count; i++) {
            me.internalDataSource.push(data[i])
          }
        }
      }
    },
    /**
     * xử lý phím esc
     */
    onEsc (e) {
      const me = this

      // ẩn dropdown và stop event nếu đang expand
      if (me.expanded) {
        me.collapse()
        e.preventDefault()
        e.cancel = true
      }
    },

    /**
     * xử lý phím enter
     */
    onEnter (e) {
      const me = this

      if (me.expanded && !me.loading && me.internalDataSource.length === 0) {
        e.preventDefault()
        e.stopPropagation()
      }
    },

    /**
     * Mở form thêm nhanh
     */
    showQuickAdd (isfromEnter, isfromShortKey, formName) {
      const me = this

      // Gọi đến sự kiện click icon thêm nhanh nhiều object nếu có config quickAddMulti trên combobox và không phải sự kiện click vào menu-item
      if (me.quickAddMulti && !formName) {
        const quickAddMultiSelector = document.querySelector('.quickAddMulti')

        if (!quickAddMultiSelector) {
          return
        }

        quickAddMultiSelector.click()

        return
      }

      // Lấy key form thêm nhanh từ config hoặc tham số truyền vào
      const quickAdd = me.quickAdd || formName

      if (!me.loading) {
        if (!quickAdd) {
          return
        }
        me.expanded = false
        const param = { ...(me.quickSearchParam || {}) }
        param.mode = me.$ms.constant.FormState.Add
        if (me.internalText) {
          param.quickAddText = me.internalText
        }

        // Modified By NXCHIEN 01.06.2022 Sửa lại do base xử lý check options.submit
        param.options = {
          submit: me.callbackAddData.bind(me),
        }

        // TODO => show popup quick add ở đây
        popupUtil.show(quickAdd, param)
      }
    },

    /**
     * @description Xóa bỏ lastRequestParam
     * @author PVThang
     */
    clearLastRequestParam () {
      this.lastRequestParam = null
    },
  },
})
</script>

<style lang="scss" scoped>
@import '@/assets/scss/components/MsCombobox.scss';
</style>
